import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { TableVirtuoso } from "react-virtuoso";
import { Box, Typography, Button } from "../../../design-system";
import ViewListIcon from "@mui/icons-material/ViewList";
import cherryLogo from "../../../../assets/brand/logo_site.svg";
import { colors } from "../../../design-system/colors";
import { messageContext } from "../../../../stores/messages/messageContext";
import { userContext } from "../../../../stores/users/userContext";
import { getUserTranscriptVideosUrl, runWhisperUrl, WHISPER_ENABLED, autoClipUrl, regenerateThumbnailsUrl, createShareUrl } from "../../../constants";
import apiRequest from "../../../../utils/apiClient";
import { getCookie } from "../../../../utils/cookie";
import { UPLOADS_EMPTY_STATE_MESSAGE, LIBRARY_SEARCH_PLACEHOLDER, LIBRARY_NO_MATCHES, LIBRARY_SORT_LABEL, FAILED_TEXT, FAILED_OVER_LIMIT_PREFIX, FAILED_SEE_PLANS } from "../constants/myLibraryText";
import { ROUTES } from "../../../../constants/routes";
import { VideoData } from "../types";
import VideoThumbnail from "../components/VideoThumbnail";
import InlineVideoPlayer from "../components/InlineVideoPlayer";
import UploadsTableHeader from "../components/UploadsTableHeader";
import { uploadsTableHeader } from "../constants/uploadsTableHeader";
import { formatDateOnly, formatTimeOnly, formatProcessingTime } from "../../../../utils/dateFormatting";
import { formatDuration } from "../../../../utils/durationFormatting";
import RowActions from "../components/RowActions";
import RenameDialog from "../components/RenameDialog";
import {
  LibraryTable,
  LibraryTableRow,
} from "../components/LibraryTableComponents";
import { ConfirmModal } from "../../../design-system";
import {
  DELETE_CONFIRM_TITLE,
  getDeleteConfirmMessage,
  BUTTON_CONFIRM,
  BUTTON_CANCEL,
  TASK_STATUS_TEXT,
  ACTION_START_EDITING,
  ACTION_TOP_CLIPS,
  ACTION_TOP_CLIPS_TITLE,
  LIBRARY_HINT,
} from "../constants/myLibraryText";
import { TaskStatus, IN_FLIGHT_STATUSES } from "../constants/taskStatuses";
import { deleteUploadTranscript } from "../utils/deleteTranscript";
import { renameUploadTranscript } from "../utils/renameTranscript";
import { useFileDownload } from "../hooks/useFileDownload";
import { useLibraryTabData } from "../hooks/useLibraryTabData";
import { useTableSort } from "../hooks/useTableSort";
import { useDeleteModal } from "../hooks/useDeleteModal";
import { useUploadTimeout } from "../hooks/useUploadTimeout";
import LibraryTabLayout from "../components/LibraryTabLayout";
import SkeletonMediaRow from "../components/SkeletonMediaRow";
import "./UploadsTab.css";
import { lookForPage, useLibraryLook } from "../looks/libraryLook";
import PaperUploads from "../looks/PaperUploads";

const uploadsColumnWidths = uploadsTableHeader.map((col) =>
  typeof col.width === "number" ? `${col.width}px` : (col.width as string),
);

// Below this much room for the buttons' column, they move under the video's name.
const ACTIONS_MIN_WIDTH = 260;
const FIXED_COLUMNS_WIDTH = uploadsColumnWidths.slice(1, -1).reduce((sum, w) => sum + parseInt(w, 10), 0);

const UploadsVirtuosoTable: React.FC<React.ComponentProps<"table">> = (
  props,
) => (
  <LibraryTable {...props}>
    <colgroup>
      {uploadsColumnWidths.map((w, i) => (
        <col key={i} style={{ width: i === 0 ? "var(--cc-clipw, 138px)" : w }} />
      ))}
    </colgroup>
    {props.children}
  </LibraryTable>
);

type UploadSort = "newest" | "oldest" | "longest" | "shortest" | "title" | "clips";
const UPLOAD_SORT_OPTIONS: { key: UploadSort; label: string }[] = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "longest", label: "Longest" },
  { key: "shortest", label: "Shortest" },
  { key: "title", label: "Title A–Z" },
  { key: "clips", label: "Most clips" },
];

const UploadsTab: React.FC = (): JSX.Element => {
  const messageStore = useContext(messageContext);
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  // Gallery / Strips / Classic: the first two are the new light looks (../looks/).
  const libraryLook = useLibraryLook();
  const look = lookForPage(libraryLook.look, useLocation().search);
  const [paperCardWidth, setPaperCardWidth] = useState<number>(
    () => Number(localStorage.getItem("cc_library_cardw")) || 300,
  );
  useEffect(() => { localStorage.setItem("cc_library_cardw", String(paperCardWidth)); }, [paperCardWidth]);

  const getVideoSortValue = useCallback(
    (video: VideoData, field: string): string | number => {
      if (field === "name_description") return video.title?.toLowerCase() || "";
      if (field === "date_added") return new Date(video.created_at).getTime();
      if (field === "duration") return video.duration_in_seconds ?? 0;
      return "";
    },
    [],
  );

  const { items: videos, setItems: setVideos, loading, error, silentRefetch: silentRefetchVideos } =
    useLibraryTabData<VideoData>(getUserTranscriptVideosUrl, messageStore, "videos");
  const { sortField, sortDirection, handleSortClick, sortedData: headerSortedVideos } =
    useTableSort<VideoData>(videos, getVideoSortValue);
  const { deleteModalOpen, itemToDelete: videoToDelete, isDeleting, handleDeleteClick, handleDeleteConfirm, handleDeleteModalClose } =
    useDeleteModal(
      deleteUploadTranscript,
      messageStore,
      (id) => setVideos((prev) => prev.filter((video) => video.id !== id)),
    );

  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [videoPlayerOpen, setVideoPlayerOpen] = useState<boolean>(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [videoToRename, setVideoToRename] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [activeVideoId, setActiveVideoId] = useState<number | null>(null);
  const [runningWhisperIds, setRunningWhisperIds] = useState<Set<number>>(new Set());
  const [queuedWhisperIds, setQueuedWhisperIds] = useState<Set<number>>(new Set());
  const [autoClippingIds, setAutoClippingIds] = useState<Set<number>>(new Set());
  const [regeneratingThumbIds, setRegeneratingThumbIds] = useState<Set<number>>(new Set());
  // View preferences (persisted): list/grid + thumbnail size.
  const [viewMode, setViewMode] = useState<"list" | "grid">(
    () => (localStorage.getItem("cc_uploads_view") as "list" | "grid") || "list",
  );
  const [cardWidth, setCardWidth] = useState<number>(
    () => Number(localStorage.getItem("cc_uploads_cardw")) || 240,
  );
  useEffect(() => { localStorage.setItem("cc_uploads_view", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("cc_uploads_cardw", String(cardWidth)); }, [cardWidth]);
  // Narrow window (or a big thumbnail): the buttons go under the name instead of being crushed.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listWidth, setListWidth] = useState(0);
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setListWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode, loading]);
  const narrow = listWidth > 0 && listWidth - cardWidth - FIXED_COLUMNS_WIDTH < ACTIONS_MIN_WIDTH;

  // Search box (title / description) + Sort menu. "Newest" keeps the server order, so the
  // column-header sorting still applies; any other choice sorts on top of it.
  const [searchText, setSearchText] = useState<string>("");
  const [sortChoice, setSortChoice] = useState<UploadSort>(
    () => (localStorage.getItem("cc_uploads_sort") as UploadSort) || "newest",
  );
  useEffect(() => { localStorage.setItem("cc_uploads_sort", sortChoice); }, [sortChoice]);
  const query = searchText.trim().toLowerCase();
  const sortedVideos = headerSortedVideos.filter((v) =>
    !query || `${v.title || ""} ${v.description || ""}`.toLowerCase().includes(query));
  if (sortChoice !== "newest") {
    const time = (v: VideoData) => new Date(v.created_at).getTime();
    const cmp: Record<UploadSort, (a: VideoData, b: VideoData) => number> = {
      newest: () => 0,
      oldest: (a, b) => time(a) - time(b),
      longest: (a, b) => (b.duration_in_seconds || 0) - (a.duration_in_seconds || 0),
      shortest: (a, b) => (a.duration_in_seconds || 0) - (b.duration_in_seconds || 0),
      title: (a, b) => (a.title || "").localeCompare(b.title || ""),
      clips: (a, b) => (b.clips_count || 0) - (a.clips_count || 0),
    };
    sortedVideos.sort(cmp[sortChoice]);
  }
  // Close any inline preview when switching view/sort so a remounted row doesn't auto-replay.
  useEffect(() => { setVideoPlayerOpen(false); }, [viewMode, sortField, sortDirection]);
  const listThumbHeight = Math.round((cardWidth * 9) / 16);

  const { download, isDownloadingId } = useFileDownload({
    onError: (msg) => messageStore.setMessage(msg, "error"),
  });

  const pendingIds = videos
    .filter((video) => video.task_status && IN_FLIGHT_STATUSES.has(video.task_status))
    .map((video) => video.id);

  // A never-completed `thumbnails_ready` flag on an OLD upload means background poster
  // generation already ENDED — the worker task is bounded (120s chunks that stop the moment
  // frames stop succeeding) and SQS-capped at 3 retries, so it is NOT still churning days later;
  // the flag just never got flipped (e.g. it crashed on a bad/vertical frame before the flip).
  // Treat generation as "live" only for RECENT uploads, so a stale flag never shows an endless
  // spinner or keeps the card polling forever.
  const POSTER_ACTIVE_WINDOW_MS = 2 * 60 * 60 * 1000; // 2h — comfortably beyond real completion
  const isPosterStillGenerating = (video: VideoData): boolean => {
    if (video.thumbnails_ready !== false) return false;
    const uploadedMs = video.created_at ? new Date(video.created_at).getTime() : NaN;
    if (!Number.isFinite(uploadedMs)) return true; // unknown age → assume fresh (safe default)
    return (Date.now() - uploadedMs) < POSTER_ACTIVE_WINDOW_MS;
  };

  // Completed videos whose poster is still generating in the background (thumbnails moved off
  // the critical path — the card poster comes from the first clip's thumbnail). Keep refreshing
  // a BOUNDED while so the card fills in without a manual reload; bounded because an audio-only
  // upload never gets a poster and would otherwise poll forever. Old stuck flags are excluded
  // (isPosterStillGenerating) so a days-old video never keeps this poll alive.
  const posterPendingIds = videos
    .filter((video) => video.task_status && !IN_FLIGHT_STATUSES.has(video.task_status)
      && video.task_status !== "failed" && isPosterStillGenerating(video))
    .map((video) => video.id);

  // Auto-update while anything is processing. SSE couldn't carry our Bearer token (it only
  // sends cookies), so it never delivered the "done" event and the row stayed "Pending" until
  // a manual refresh. Polling the list every few seconds is reliable and carries auth, so the
  // row flips to the finished clip on its own — and now also fills in late background posters.
  useEffect(() => {
    if (pendingIds.length === 0 && posterPendingIds.length === 0) return;
    let posterOnlyRounds = 0;
    const MAX_POSTER_ROUNDS = 48; // ~4 min at 5s: enough for posters, bounded for audio-only
    const interval = setInterval(() => {
      // Once nothing is transcribing and only posters are outstanding, cap the extra polling.
      if (pendingIds.length === 0) {
        posterOnlyRounds += 1;
        if (posterOnlyRounds > MAX_POSTER_ROUNDS) { clearInterval(interval); return; }
      }
      silentRefetchVideos();
    }, 5000);
    return () => clearInterval(interval);
  }, [pendingIds.length, posterPendingIds.length, silentRefetchVideos]);

  useUploadTimeout({
    videos,
    setVideos,
    error,
    onError: (msg) => messageStore.setMessage(msg, "error"),
  });

  const handleVideoClick = (video: VideoData): void => {
    // A video that didn't become clips has nothing to open (its row says why).
    if (video.task_status === "failed") return;
    userStore.setUserTranscript(null, video.title, video.id, 0);
    navigate("/create-video/");
  };

  // ARCHIVE — Combine mode: tick several finished uploads and open them as ONE sheet (/archive/).
  const [combineMode, setCombineMode] = useState<boolean>(false);
  const [combinePicked, setCombinePicked] = useState<Set<number>>(() => new Set());
  const toggleCombine = (id: number): void =>
    setCombinePicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const openCombined = (): void => {
    if (combinePicked.size) navigate(`/archive/?ids=${Array.from(combinePicked).join(",")}`);
  };

  const handleClipClick = (video: VideoData): void => {
    setSelectedVideo(video);
    setVideoPlayerOpen(true);
  };

  const handleDownloadTranscript = (videoId: number): void => {
    const video = videos.find((video) => video.id === videoId);
    if (video) {
      setVideoPlayerOpen(false);
      handleVideoClick(video);
    }
  };

  const handleRenameClick = (videoId: number, currentTitle: string): void => {
    setVideoToRename({ id: videoId, title: currentTitle });
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async (newTitle: string): Promise<void> => {
    if (videoToRename && newTitle.trim() !== videoToRename.title) {
      setIsRenaming(true);
      const success = await renameUploadTranscript(
        videoToRename.id,
        newTitle,
        messageStore,
      );
      setIsRenaming(false);
      if (success) {
        setVideos((prev) =>
          prev.map((video) =>
            video.id === videoToRename.id ? { ...video, title: newTitle } : video,
          ),
        );
        setRenameDialogOpen(false);
        setVideoToRename(null);
      }
    } else {
      setRenameDialogOpen(false);
      setVideoToRename(null);
    }
  };

  const handleRunWhisper = async (transcriptId: number): Promise<void> => {
    if (queuedWhisperIds.has(transcriptId)) return;
    setRunningWhisperIds((prev) => new Set(prev).add(transcriptId));
    try {
      const res = await apiRequest(runWhisperUrl, {
        method: "POST",
        body: JSON.stringify({ transcript_id: transcriptId }),
        headers: { "Content-Type": "application/json" },
      });
      const body = await res.json();
      if (res.ok) {
        setQueuedWhisperIds((prev) => new Set(prev).add(transcriptId));
        messageStore.setMessage(body.message || "Whisper queued!", "info");
      } else {
        messageStore.setMessage(body.error || "Failed to queue Whisper", "error");
      }
    } catch {
      messageStore.setMessage("Failed to queue Whisper", "error");
    } finally {
      setRunningWhisperIds((prev) => { const s = new Set(prev); s.delete(transcriptId); return s; });
    }
  };

  // Create a shareable ET link for this video and copy it to the clipboard.
  const handleShare = async (transcriptId: number): Promise<void> => {
    try {
      const formData = new URLSearchParams();
      formData.append("transcript_id", String(transcriptId));
      formData.append("permission_level", "export");
      const res = await apiRequest(createShareUrl, {
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": getCookie("csrftoken"),
        },
      });
      const body = await res.json();
      if (res.ok && body.token) {
        const url = `${window.location.origin}${body.url_path}`;
        try { await navigator.clipboard.writeText(url); } catch (e) { /* clipboard may be blocked */ }
        messageStore.setMessage("🔗 Share link copied! Anyone with it can open this clip editor.", "success", 8000);
      } else {
        messageStore.setMessage(body.message || "Couldn't create a share link.", "error");
      }
    } catch {
      messageStore.setMessage("Couldn't create a share link. Please try again.", "error");
    }
  };

  // One-click 🍒 Auto-Clip: ask the backend to build a "highlights supercut" export
  // (all auto-highlights, sorted short→long, capped at 30s) — no AI, no API cost.
  // The result lands in My Library → Exported like any manual export.
  const handleAutoClip = async (transcriptId: number): Promise<void> => {
    if (autoClippingIds.has(transcriptId)) return;
    setAutoClippingIds((prev) => new Set(prev).add(transcriptId));
    try {
      const formData = new URLSearchParams();
      formData.append("transcript_id", String(transcriptId));
      formData.append("recipe", "highlights_supercut");
      const res = await apiRequest(autoClipUrl, {
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": getCookie("csrftoken"),
        },
      });
      const body = await res.json();
      if (res.ok && body.message === "success") {
        messageStore.setMessage(
          "🍒 Your Highlights Supercut is processing!",
          "success",
          8000,
          { text: "View in My Library", url: "/my-library/exports/" },
        );
      } else {
        messageStore.setMessage(
          body.message || "Couldn't create clips for this video.",
          "error",
        );
      }
    } catch {
      messageStore.setMessage("Couldn't create clips. Please try again.", "error");
    } finally {
      setAutoClippingIds((prev) => { const s = new Set(prev); s.delete(transcriptId); return s; });
    }
  };

  // Regenerate a video's poster thumbnails — clears the cached JPEGs and re-runs the
  // background generate_thumbnails task. Handy for fixing stale/failed/stretched posters
  // (e.g. the rotation-aware poster fix) without re-uploading + re-transcribing.
  const handleRegenerateThumbnails = async (transcriptId: number): Promise<void> => {
    if (regeneratingThumbIds.has(transcriptId)) return;
    setRegeneratingThumbIds((prev) => new Set(prev).add(transcriptId));
    try {
      const formData = new URLSearchParams();
      formData.append("transcript_id", String(transcriptId));
      const res = await apiRequest(regenerateThumbnailsUrl, {
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": getCookie("csrftoken"),
        },
      });
      const body = await res.json();
      if (res.ok && body.message === "success") {
        messageStore.setMessage(
          "🖼️ Regenerating thumbnails — they will refresh shortly. Reload the page in a bit to see them.",
          "success",
          8000,
        );
      } else {
        messageStore.setMessage(
          body.message || "Couldn't regenerate thumbnails for this video.",
          "error",
        );
      }
    } catch {
      messageStore.setMessage("Couldn't regenerate thumbnails. Please try again.", "error");
    } finally {
      setRegeneratingThumbIds((prev) => { const s = new Set(prev); s.delete(transcriptId); return s; });
    }
  };

  // The ⋮ menu, the same in every look.
  const renderMenu = (video: VideoData): React.ReactNode => (
    <RowActions
      videoId={video.id}
      videoTitle={video.title}
      videoUrl={video.video}
      onDownload={(id, url) => download(id, url, video.title, "mp4")}
      isDownloading={isDownloadingId(video.id)}
      onDelete={handleDeleteClick}
      onRename={handleRenameClick}
      onRunWhisper={WHISPER_ENABLED && video.transcription_engine !== "whisper" && !queuedWhisperIds.has(video.id) ? handleRunWhisper : undefined}
      isRunningWhisper={runningWhisperIds.has(video.id)}
      onAutoClipMenu={handleAutoClip}
      isAutoClipping={autoClippingIds.has(video.id)}
      onShare={handleShare}
      // A repair tool, not something people need: admins only in the new looks.
      onRegenerateThumbnails={libraryLook.canEdit ? handleRegenerateThumbnails : undefined}
      isRegeneratingThumbnails={regeneratingThumbIds.has(video.id)}
    />
  );

  const dialogs = (
    <>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        title={DELETE_CONFIRM_TITLE}
        message={videoToDelete ? getDeleteConfirmMessage(videoToDelete.title) : ""}
        confirmLabel={BUTTON_CONFIRM}
        cancelLabel={BUTTON_CANCEL}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteModalClose}
        loading={isDeleting}
      />
      <RenameDialog
        open={renameDialogOpen}
        currentTitle={videoToRename?.title || ""}
        onClose={() => { setRenameDialogOpen(false); setVideoToRename(null); }}
        onConfirm={handleRenameConfirm}
        loading={isRenaming}
      />
    </>
  );

  if (look !== "classic") {
    return (
      <>
        {loading ? (
          <div className="hint">…</div>
        ) : error ? (
          <div className="hint">{error}</div>
        ) : videos.length === 0 ? (
          <div className="hint">{UPLOADS_EMPTY_STATE_MESSAGE}</div>
        ) : (
          <PaperUploads
            look={look}
            videos={sortedVideos}
            allVideos={videos}
            searchText={searchText}
            onSearchText={setSearchText}
            sortChoice={sortChoice}
            sortOptions={UPLOAD_SORT_OPTIONS}
            onSortChoice={(key) => setSortChoice(key as UploadSort)}
            cardWidth={paperCardWidth}
            onCardWidth={setPaperCardWidth}
            combineMode={combineMode}
            picked={combinePicked}
            onTogglePick={toggleCombine}
            onToggleCombine={() => { setCombineMode((m) => !m); setCombinePicked(new Set()); }}
            onOpenCombined={openCombined}
            onOpenClips={handleVideoClick}
            onTop20={(video) => navigate(`${ROUTES.REEL}?t=${video.id}`)}
            renderMenu={renderMenu}
            onShare={handleShare}
            onOpenBlackCherry={(video) => {
              userStore.setUserTranscript(null, video.title, video.id, 0);
              navigate("/create-video/?blackcherry=1");
            }}
            onOpenExporter={(video, clipIds) => {
              userStore.setUserTranscript(null, video.title, video.id, 0);
              navigate(`/create-video/?picks=${clipIds.join(",")}&export=1`);
            }}
            isInFlight={(video) => !!(video.task_status && IN_FLIGHT_STATUSES.has(video.task_status))}
          />
        )}
        {dialogs}
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }} style={{ ["--cc-clipw" as any]: `${cardWidth}px` }}>
    <LibraryTabLayout
      loading={loading}
      error={error}
      isEmpty={videos.length === 0}
      emptyMessage={UPLOADS_EMPTY_STATE_MESSAGE}
    >
      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", color: "#b9b3bd", padding: "6px 24px 0" }}>
        {LIBRARY_HINT}
      </Typography>
      {/* Toolbar: view toggle + thumbnail size slider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 24px" }}>
        {viewMode === "list" && (
          <Box
            onClick={() => { setCombineMode((m) => !m); setCombinePicked(new Set()); }}
            title="Open several videos as ONE sheet — filter, search, tick and export across them (Archive)"
            sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 700, color: combineMode ? "#fff" : "#bbb", background: combineMode ? "#e53935" : "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "8px", padding: "5px 12px", whiteSpace: "nowrap" }}
          >
            🗂 {combineMode ? "Combining — tick the videos" : "Combine into one sheet"}
          </Box>
        )}
        <input
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={LIBRARY_SEARCH_PLACEHOLDER}
          style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontFamily: "Outfit, sans-serif", fontSize: "13px", padding: "7px 12px", width: "220px", outline: "none" }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#888" }}>{LIBRARY_SORT_LABEL}</Typography>
          <select
            value={sortChoice}
            onChange={(e) => setSortChoice(e.target.value as UploadSort)}
            style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: "8px", color: "#fff", fontFamily: "Outfit, sans-serif", fontSize: "13px", padding: "6px 8px", outline: "none", cursor: "pointer" }}
          >
            {UPLOAD_SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#888" }}>Size</Typography>
          <input type="range" min={160} max={380} step={10} value={cardWidth} onChange={(e) => setCardWidth(Number(e.target.value))} style={{ width: "120px", accentColor: "#e53935" }} />
        </Box>
        <Box sx={{ display: "flex", gap: "4px", background: "#1a1a1a", borderRadius: "8px", padding: "3px" }}>
          {(["list", "grid"] as const).map((key) => (
            <Box key={key} onClick={() => setViewMode(key)} sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: viewMode === key ? "#fff" : "#888", background: viewMode === key ? "#e53935" : "transparent", borderRadius: "6px", padding: "5px 12px", whiteSpace: "nowrap" }}>
              {key === "list" ? "☰ List" : "▦ Grid"}
            </Box>
          ))}
        </Box>
      </Box>

      {combineMode && combinePicked.size > 0 && (
        <Box sx={{ position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 1600, display: "flex", alignItems: "center", gap: "10px", background: "#141018", border: "1px solid #e53935", borderRadius: "999px", padding: "8px 10px 8px 16px", boxShadow: "0 10px 30px rgba(0,0,0,0.6)" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", color: "#eee" }}>
            {combinePicked.size} video{combinePicked.size === 1 ? "" : "s"} picked
          </Typography>
          <Box onClick={openCombined} sx={{ cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 800, color: "#fff", background: "#e53935", borderRadius: "999px", padding: "6px 14px" }}>
            Open as one sheet →
          </Box>
        </Box>
      )}

      {viewMode === "list" ? (
      // A window too small even for the fixed columns scrolls sideways, header and rows together.
      <Box ref={listRef} sx={{ display: "flex", flex: 1, minHeight: 0, overflowX: "auto" }}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <UploadsVirtuosoTable>
        <thead>
          <UploadsTableHeader
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={(field: string) => { setSortChoice("newest"); handleSortClick(field); }}
          />
        </thead>
      </UploadsVirtuosoTable>
      {query && sortedVideos.length === 0 && (
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "14px", color: "#888", padding: "24px" }}>{LIBRARY_NO_MATCHES}</Typography>
      )}
      <TableVirtuoso
        data={sortedVideos}
        style={{ flex: 1, width: "100%", overflow: "auto" }}
        components={
          {
            Table: UploadsVirtuosoTable,
            TableRow: LibraryTableRow,
          } as React.ComponentProps<typeof TableVirtuoso>["components"]
        }
        itemContent={(index) => {
          const video = sortedVideos[index];

          if (video.task_status && IN_FLIGHT_STATUSES.has(video.task_status)) {
            return (
              <SkeletonMediaRow
                statusText={video.task_status === TaskStatus.PENDING ? TASK_STATUS_TEXT.PENDING : TASK_STATUS_TEXT.TRANSCRIBING}
                columnWidths={uploadsColumnWidths}
              />
            );
          }

          // The row's buttons: in their own column, or under the name when the window is narrow.
          const actions = (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: narrow ? "flex-start" : "flex-end",
                    gap: "8px",
                    marginTop: narrow ? "12px" : 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* A video that didn't become clips gets no editor buttons, just why (its name
                      says which) and, when it's the plan limit, the way out. Delete stays in ⋮. */}
                  {video.task_status === "failed" ? (
                    <>
                      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 700, color: "#ff6b81", whiteSpace: "nowrap" }}>
                        {FAILED_TEXT}
                      </Typography>
                      {video.title?.startsWith(FAILED_OVER_LIMIT_PREFIX) && (
                        <Button
                          variant="primary"
                          onClick={(e) => { e.stopPropagation(); navigate(ROUTES.PRICING); }}
                          sx={{ height: "34px", fontSize: "14px", padding: "0 14px", whiteSpace: "nowrap" }}
                        >
                          {FAILED_SEE_PLANS}
                        </Button>
                      )}
                    </>
                  ) : (
                  <>
                  {/* The two paths, always visible: CherryPick (you pick — the hero) and
                      Auto-Clip (let CherryClipz make them). The ⋮ holds the rest. */}
                  <Button
                    variant="primary"
                    onClick={(e) => { e.stopPropagation(); handleVideoClick(video); }}
                    aria-label="Open clips — this video's clip spreadsheet"
                    startIcon={<img src={cherryLogo} alt="" style={{ height: 15, width: "auto", filter: "brightness(0) invert(1)" }} />}
                    sx={{ height: "34px", fontSize: "14px", padding: "0 14px", whiteSpace: "nowrap" }}
                  >
                    {ACTION_START_EDITING}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={(e) => { e.stopPropagation(); navigate(`${ROUTES.REEL}?t=${video.id}`); }}
                    aria-label={ACTION_TOP_CLIPS_TITLE}
                    title={ACTION_TOP_CLIPS_TITLE}
                    startIcon={<ViewListIcon sx={{ fontSize: 16 }} />}
                    sx={{ height: "34px", minHeight: "34px", fontSize: "14px", padding: "0 14px", whiteSpace: "nowrap" }}
                  >
                    {ACTION_TOP_CLIPS}
                  </Button>
                  </>
                  )}
                  <RowActions
                    videoId={video.id}
                    videoTitle={video.title}
                    videoUrl={video.video}
                    onDownload={(id, url) =>
                      download(id, url, video.title, "mp4")
                    }
                    isDownloading={isDownloadingId(video.id)}
                    onDelete={handleDeleteClick}
                    onRename={handleRenameClick}
                    onRunWhisper={
                      WHISPER_ENABLED && video.transcription_engine !== "whisper" && !queuedWhisperIds.has(video.id)
                        ? handleRunWhisper
                        : undefined
                    }
                    isRunningWhisper={runningWhisperIds.has(video.id)}
                    onAutoClipMenu={handleAutoClip}
                    isAutoClipping={autoClippingIds.has(video.id)}
                    onShare={handleShare}
                    onRegenerateThumbnails={libraryLook.canEdit ? handleRegenerateThumbnails : undefined}
                    isRegeneratingThumbnails={regeneratingThumbIds.has(video.id)}
                  />
                </Box>
          );
          return (
            <>
              <td
                style={{ width: uploadsColumnWidths[0], verticalAlign: "top" }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {videoPlayerOpen && selectedVideo?.id === video.id ? (
                    <InlineVideoPlayer
                      videoUrl={video.video ?? ""}
                      heightPx={listThumbHeight}
                      onStop={() => setVideoPlayerOpen(false)}
                    />
                  ) : (
                    <VideoThumbnail
                      videoUrl={video.video ?? ""}
                      thumbnailUrl={video.thumbnail ?? ""}
                      heightPx={listThumbHeight}
                      cherryFallback
                      onClick={() => handleClipClick(video)}
                    />
                  )}
                </Box>
              </td>
              <td
                style={{
                  width: uploadsColumnWidths[1],
                  verticalAlign: "top",
                  cursor: "pointer",
                }}
                onClick={() => (combineMode ? toggleCombine(video.id) : handleVideoClick(video))}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: "24px",
                  }}
                >
                  {combineMode && (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontFamily: "Outfit, sans-serif", fontSize: 12, color: combinePicked.has(video.id) ? "#fff" : "#999" }}>
                      <input type="checkbox" checked={combinePicked.has(video.id)} readOnly style={{ accentColor: "#e53935" }} />
                      {combinePicked.has(video.id) ? "In the sheet" : "Add to the sheet"}
                    </label>
                  )}
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: colors.neutral.white,
                      lineHeight: "22px",
                    }}
                  >
                    {video.title}
                  </Typography>
                  {/* clip length, sitting a little lower + bolder under the title */}
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: colors.text[700],
                      lineHeight: "16px",
                      marginTop: "5px",
                    }}
                  >
                    {video.duration_in_seconds
                      ? formatDuration(video.duration_in_seconds)
                      : "—"}
                  </Typography>
                  {/* Thumbnails moved off the critical path — the clip is openable NOW while
                      posters still generate in the background. Lead with "ready" (green) so it
                      reads as "use me now", with the poster status as a quiet secondary note. */}
                  {isPosterStillGenerating(video) && (
                    <Typography
                      sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontSize: "11px",
                        fontWeight: 600,
                        lineHeight: "15px",
                        marginTop: "5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Box component="span" sx={{ color: "#22c55e", fontWeight: 700 }}>
                        ✓ Ready — open &amp; cherry-pick now
                      </Box>
                      <Box component="span" sx={{ color: colors.text[700], fontWeight: 500, display: "flex", alignItems: "center", gap: "5px" }}>
                        · posters still filling in
                        <Box
                          component="span"
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor: colors.text[700],
                            borderTopColor: "transparent",
                            animation: "spin 0.9s linear infinite",
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    </Typography>
                  )}
                  {narrow && actions}
                </Box>
              </td>
              {/* Clipz count — how many clips were generated from this video */}
              <td
                style={{ width: uploadsColumnWidths[2], verticalAlign: "top", cursor: "pointer" }}
                onClick={() => handleVideoClick(video)}
              >
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#e61d50",
                      lineHeight: "20px",
                    }}
                  >
                    {typeof video.clips_count === "number" ? video.clips_count : "—"}
                  </Typography>
                </Box>
              </td>
              <td
                style={{ width: uploadsColumnWidths[3], verticalAlign: "top", cursor: "pointer" }}
                onClick={() => handleVideoClick(video)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: "24px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: colors.neutral.white,
                      lineHeight: "20px",
                    }}
                  >
                    {formatDateOnly(video.created_at)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: colors.text[700],
                      lineHeight: "16px",
                      marginTop: "5px",
                    }}
                  >
                    {formatTimeOnly(video.created_at)}
                  </Typography>
                </Box>
              </td>
              {/* Processing Time — how long from upload to clips-ready */}
              <td
                style={{ width: uploadsColumnWidths[4], verticalAlign: "top", cursor: "pointer" }}
                onClick={() => handleVideoClick(video)}
              >
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "24px" }}>
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "14px",
                      fontWeight: 400,
                      color: colors.neutral.white,
                      lineHeight: "20px",
                    }}
                  >
                    {formatProcessingTime(video.created_at, video.processed_at) || "—"}
                  </Typography>
                </Box>
              </td>
              <td style={{ verticalAlign: "top" }}>{!narrow && actions}</td>
            </>
          );
        }}
      />
      </Box>
      </Box>
      ) : (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", padding: "16px 24px", overflowY: "auto", alignContent: "flex-start", flex: 1 }}>
        {sortedVideos.map((video) => {
          const inFlight = !!(video.task_status && IN_FLIGHT_STATUSES.has(video.task_status));
          const thumbH = Math.round((cardWidth * 9) / 16);
          return (
            <Box key={video.id} sx={{ width: `${cardWidth}px`, background: "#161616", borderRadius: "10px", overflow: "hidden", border: "1px solid #2a2a2a", display: "flex", flexDirection: "column" }}>
              <Box
                onClick={inFlight || video.task_status === "failed" ? undefined : () => handleVideoClick(video)}
                sx={{ width: "100%", height: `${thumbH}px`, background: "#000", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", cursor: inFlight ? "default" : "pointer" }}
              >
                {inFlight ? (
                  <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", border: "3px solid #e53935", borderTopColor: "transparent", animation: "spin 0.9s linear infinite" }} />
                ) : video.thumbnail ? (
                  <img src={video.thumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
                ) : (
                  <img src={cherryLogo} alt="" style={{ height: "55%", width: "auto", opacity: 0.85 }} />
                )}
              </Box>
              <Box sx={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography title={video.title} sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: colors.neutral.white, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {video.title}
                </Typography>
                {inFlight ? (
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#e53935" }}>
                    {video.task_status === TaskStatus.PENDING ? TASK_STATUS_TEXT.PENDING : TASK_STATUS_TEXT.TRANSCRIBING}
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: video.task_status === "failed" ? "#ff6b81" : "#e61d50", fontWeight: 600 }}>
                        {video.task_status === "failed" ? FAILED_TEXT : typeof video.clips_count === "number" ? `${video.clips_count} clipz` : "—"}
                      </Typography>
                      {video.duration_in_seconds ? <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#aaa" }}>· {formatDuration(video.duration_in_seconds)}</Typography> : null}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "2px" }}>
                      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#666" }}>{formatDateOnly(video.created_at)}</Typography>
                      <RowActions
                        videoId={video.id}
                        videoTitle={video.title}
                        videoUrl={video.video}
                        onDownload={(id, url) => download(id, url, video.title, "mp4")}
                        isDownloading={isDownloadingId(video.id)}
                        onDelete={handleDeleteClick}
                        onRename={handleRenameClick}
                        onRunWhisper={WHISPER_ENABLED && video.transcription_engine !== "whisper" && !queuedWhisperIds.has(video.id) ? handleRunWhisper : undefined}
                        isRunningWhisper={runningWhisperIds.has(video.id)}
                        onAutoClipMenu={handleAutoClip}
                      showReel
                        isAutoClipping={autoClippingIds.has(video.id)}
                        onShare={handleShare}
                        onRegenerateThumbnails={libraryLook.canEdit ? handleRegenerateThumbnails : undefined}
                        isRegeneratingThumbnails={regeneratingThumbIds.has(video.id)}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      )}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        title={DELETE_CONFIRM_TITLE}
        message={
          videoToDelete ? getDeleteConfirmMessage(videoToDelete.title) : ""
        }
        confirmLabel={BUTTON_CONFIRM}
        cancelLabel={BUTTON_CANCEL}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteModalClose}
        loading={isDeleting}
      />
      <RenameDialog
        open={renameDialogOpen}
        currentTitle={videoToRename?.title || ""}
        onClose={() => {
          setRenameDialogOpen(false);
          setVideoToRename(null);
        }}
        onConfirm={handleRenameConfirm}
        loading={isRenaming}
      />
    </LibraryTabLayout>
    </Box>
  );
};

export default observer(UploadsTab);
